import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SolicitarDados = () => {
  const [contato, setContato] = useState('');
  const [tipo, setTipo] = useState('acesso');
  const [mensagem, setMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [enviado, setEnviado] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    if (contato.trim().length < 5) { setError('Informe um e-mail ou telefone válido.'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/lgpd/solicitacoes`, { contato: contato.trim(), tipo, mensagem: mensagem.trim() || null });
      setEnviado(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-yellow-50 text-gray-900">
      <header className="bg-black border-b-4 border-yellow-400 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="lgpd-back-home" className="inline-flex items-center gap-2 text-gray-300 hover:text-yellow-400 font-semibold text-sm transition-colors">
            <ArrowLeft className="w-5 h-5" /><span className="hidden sm:inline">Voltar ao site</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-wing-white.png" alt="WING Academy Brasil" className="h-10 sm:h-12 md:h-14 w-auto object-contain" />
          </Link>
          <span className="w-[110px] hidden sm:inline-block" aria-hidden="true"></span>
          <span className="w-5 sm:hidden" aria-hidden="true"></span>
        </div>
      </header>

      <section className="container mx-auto px-4 pt-12 pb-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-2xl mb-5 shadow-lg shadow-yellow-200">
          <ShieldCheck className="w-8 h-8 text-black" />
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-3 leading-tight">
          Solicitação de <span className="text-yellow-500">Dados (LGPD)</span>
        </h1>
        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          Você pode solicitar o <strong>acesso</strong> ou a <strong>exclusão</strong> dos seus dados pessoais.
          Atenderemos sua solicitação conforme a Lei Geral de Proteção de Dados.
        </p>
      </section>

      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-lg mx-auto">
          {enviado ? (
            <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-8 text-center" data-testid="lgpd-enviado">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-5">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">Solicitação registrada!</h2>
              <p className="text-gray-600 mb-6">Recebemos seu pedido e entraremos em contato pelo canal informado para atendê-lo.</p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 hover:bg-black text-white font-bold transition-all">
                <ArrowLeft className="w-5 h-5" /> Voltar para a WING Academy
              </Link>
            </div>
          ) : (
            <form onSubmit={enviar} className="bg-white rounded-2xl border border-yellow-200 shadow-sm p-6 sm:p-8 space-y-4" data-testid="lgpd-form">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">E-mail ou telefone cadastrado</label>
                <input type="text" value={contato} onChange={(e) => setContato(e.target.value)} data-testid="lgpd-contato-input" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" placeholder="voce@email.com ou (11) 99999-9999" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Tipo de solicitação</label>
                <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="lgpd-tipo-select" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500">
                  <option value="acesso">Acessar meus dados</option>
                  <option value="exclusao">Excluir meus dados</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Mensagem <span className="text-gray-400 font-normal">(opcional)</span></label>
                <textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={3} maxLength={1000} data-testid="lgpd-mensagem" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" placeholder="Detalhe seu pedido, se quiser." />
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="lgpd-error">{error}</div>}
              <button type="submit" disabled={loading} data-testid="lgpd-submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 text-black font-bold transition-all">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                {loading ? 'Enviando...' : 'Enviar solicitação'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default SolicitarDados;
